import React, { useState, useRef, useEffect } from 'react';
import './SearchBar.scss';
import { SEARCH_BAR } from '../../mock/lang/generalTranslations';
import { IoSearch } from 'react-icons/io5';
import { RiCloseLargeFill } from 'react-icons/ri';
import locales from '../../assets/const/locales';
import { useCurrentLocale } from 'widget-shared-components-library';
import NoResults from '../../../src/assets/images/no_results.png';

import SearchInput from './components/SearchInput';
import DefaultLayout from './components/DefaultLayout';
import SectionsLayout from './components/SectionsLayout';

const SearchBar = () => {
    const [query, setQuery] = useState('');
    const [suggestion, setSuggestion] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const recognitionRef = useRef(null); // Use ref to track recognition instance
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const inputRef = useRef(null); // Reference for the input field
    const [apiData, setApiData] = useState(null); // State to store fetched data
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const debounceTimeout = useRef(null);
    const [recentSearchesList, setRecentSearchesList] = useState(
        JSON.parse(sessionStorage.getItem('recentSearchesList'))
    );
    const [suggestionsList, setSuggestionsList] = useState([]);
    const localHost = 'http://localhost:8088';
    const isLocalEnv = process.env.NODE_ENV === 'development';
    const origin = process.env.REACT_APP_SEARCH_SUPPORT_ORIGIN_URL;
    const baseUrl = isLocalEnv ? localHost : origin;
    const lang = useCurrentLocale();

    const landingUrl =
        baseUrl +
        (lang === locales.ENGLISH || lang === 'En' || lang === 'EN'
            ? '/search-hub/search?lang=en'
            : '/search-hub/search?lang=el');

    const typeaheadUrl =
        baseUrl +
        (lang === locales.ENGLISH || lang === 'En' || lang === 'EN'
            ? '/search-hub/searchWithParameters?lang=en&q={query}'
            : '/search-hub/searchWithParameters?lang=el&q={query}');

    const originUrlRedirection = isLocalEnv ? 'https://www.cosmote.gr' : window.location.origin;
    //const originUrlRedirection = baseUrl;

    const dropdownRef = useRef(null); // Ref for the dropdown
    const [isHovered, setIsHovered] = useState(false); // State to track hover
    const searchbarRef = useRef(null); // Ref for the search bar

    const [hasScrolled, setHasScrolled] = useState(false);

    // const [latestApiData, setLatestApiData] = useState(null);



    const fetchData = async (endpoint) => {
        setIsLoading(true);
        try {
            const response = await fetch(endpoint);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setApiData(data);
            console.log("response", data);
            const sanitizedList = data?.autocomplete?.map((item) =>
                item.replace(/<em>/g, '').replace(/<\/em>/g, '')
            );
            setSuggestionsList(sanitizedList || []);
        } catch (error) {
            console.error('Fetch error:', error);
            setApiData(null);
            setSuggestionsList([]);
        } finally {
            setIsLoading(false);
        }

    };

    useEffect(()=> {
        autocomplete(query);
    },[apiData]);

    //
    // // Save the latest loaded apiData
    // useEffect(() => {
    //     if (!isLoading && apiData) {
    //         setLatestApiData(apiData);
    //     }
    // }, [isLoading, apiData]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setQuery(value);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            setDebouncedQuery(value);
        }, 300);

      autocomplete(value);
    };

    const autocomplete = (value) => {
        const match = suggestionsList?.find((item) =>
            item.toLowerCase().startsWith(value.toLowerCase())
        );
        setSuggestion(match ? match : '');
    }

    // Fetch data when the search bar is clicked and empty
    const handleInputFocus = () => {
        setIsFocused(true);
        setHover(false);
        if (!query) {
            fetchData(landingUrl);
        }
    };

    const viewAllRedirection = (type) => {
        const shopResultsUrl =
            '/eshop/searchHubResults.jsp?q={query}&selectedTabOption={selectedTabOption}';
        const tabOptions = {
            support: 'support',
            articles: 'articles',
            products: 'products',
            rateplans: 'rateplans',
        };
        const selectedTabOption = tabOptions[type] || 'products';
        const finalUrl =
            originUrlRedirection +
            shopResultsUrl
                .replace('{query}', query)
                .replace('{selectedTabOption}', selectedTabOption);

        window.location.href = finalUrl;
    };

    // Function to handle voice recognition
    const handleVoiceInput = () => {
        if (!('webkitSpeechRecognition' in window)) {
            console.log('Your browser does not support speech recognition.');
            return;
        }

        if (!recognitionRef.current) {
            recognitionRef.current = new window.webkitSpeechRecognition();
            recognitionRef.current.lang = lang === 'en' ? 'en-US' : 'el-GR';
            recognitionRef.current.interimResults = false;

            recognitionRef.current.onstart = () => {
                setIsListening(true);
            };

            recognitionRef.current.onresult = (event) => {
                const speechResult = event.results[0][0].transcript;
                setQuery(speechResult); // Set the recognized text to the input
                // Explicitly trigger suggestions after voice input
                setTimeout(() => {
                    handleInputChange({target: {value: speechResult}});
                }, 0); // Ensures that setQuery completes first
            };

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }

        // If recognition is already running, stop it
        if (recognitionRef.current && isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            // Start recognition if not already listening
            recognitionRef.current.start();
            setIsListening(true);
        }
    };

    const highlightMatchedText = (text, query) => {
        if (!query) return text;

        const parts = text.split(new RegExp(`(${query})`, 'gi'));

        return (
            <>
                {parts.map((part, index) =>
                    part.toLowerCase() === query.toLowerCase() ? (
                        <span key={index} style={{ color: '#004B87' }}>
                            {part}
                        </span>
                    ) : (
                        part
                    )
                )}
            </>
        );
    };



    const translation = (code) => {
        return SEARCH_BAR?.[code][lang === 'en' ? 'en' : 'gr'];
    };

    const handleProductClick = (product) => {
        const url = product?.productUrl;
        if (url) {
            window.location.href = originUrlRedirection + url;
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowRight' && suggestion) {
            setQuery(suggestion);
            setSuggestion('');
        } else if (e.key === 'Enter' && isFocused && query.trim()) {
            e.preventDefault();
            if (!recentSearchesList) {
                setRecentSearchesList([]);
            }
            const index = recentSearchesList?.indexOf(query);
            if (recentSearchesList && recentSearchesList?.length < 3 && index === -1) {
                recentSearchesList.unshift(query);
                sessionStorage.setItem('recentSearchesList', JSON.stringify(recentSearchesList));
            } else if (index === -1) {
                recentSearchesList.pop();
                recentSearchesList.unshift(query);
                sessionStorage.setItem('recentSearchesList', JSON.stringify(recentSearchesList));
            }
            let searchUrl = `${originUrlRedirection}/eshop/searchHubResults.jsp?q=${query}&selectedTabOption=products`;
            if (apiData?.direct_link) {
                searchUrl = `${apiData?.direct_link}`;

            }

             window.location.href = searchUrl;
        }
    };
    const handleSuggestionClick = () => {
        setQuery(suggestion);
        setSuggestion('');
        if (inputRef.current) {
            inputRef.current.textContent = suggestion;
            placeCaretAtEnd(inputRef.current);
        }
    };

    const placeCaretAtEnd = (el) => {
        el.focus();
        const selection = document.getSelection();
        selection.selectAllChildren(el);
        selection.collapseToEnd();
    };




    const removeSearch = (search) => {
        const updatedList = recentSearchesList.filter((item) => item !== search);
        setRecentSearchesList(updatedList);
        sessionStorage.setItem('recentSearchesList', JSON.stringify(updatedList));
    };


    const clearInput = () => {
        setQuery('');
        setSuggestion('');
        setApiData(null);
        fetchData(landingUrl);
    };

    const handleCloseDropdown = () => {
        setIsFocused(false);
        setIsMobileSearchOpen(false);
        setHasScrolled(false);
    };

    // Function to update window width
    const handleResize = () => {
        setWindowWidth(window.innerWidth);
        if (window.innerWidth > 990) {
            setIsMobileSearchOpen(false);
        }
    };

    const handleMobileSearchToggle = () => {
        setIsMobileSearchOpen(true);
        setHover(false);
        if (!isMobileSearchOpen) {
            setTimeout(() => {
                inputRef.current.focus(); // Automatically focus on the input field
            }, 100); // Small delay to ensure the input is rendered before focusing
        }
    };

    const handleClickOutside = (event) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target) &&
            !isHovered &&
            !searchbarRef.current.contains(event.target)
        ) {
            setIsFocused(false);
            setIsMobileSearchOpen(false);
        }
    };

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    //event listener on component mount to track window resize
    useEffect(() => {
        window.addEventListener('resize', handleResize);

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isHovered]);

    useEffect(() => {
        if (!isInitialized) {
            setIsInitialized(true);
            return;
        }
        if (debouncedQuery) {
            fetchData(typeaheadUrl.replace('{query}', debouncedQuery));
        } else {
            fetchData(landingUrl);
            setApiData(null);
            setSuggestionsList([]);
        }
    }, [debouncedQuery]);
    const [resultsStyle, setResultsStyle] = useState({});
    useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setHasScrolled(window.scrollY >= 320);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const positionResults = () => {
        if (inputRef.current) {
            const rect = inputRef.current.offsetTop +inputRef.current. offsetHeight;
            const offset = window.innerWidth > 990 ?( !hasScrolled ? 79 : 42 ): (!hasScrolled ? 40 :18);

            setResultsStyle({
                position: 'fixed',
                top: `${rect + offset}px`,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                backgroundColor: 'white',
                overflowY: 'auto',
                padding: '20px'
            });
            console.log(rect);
        }

    };
    useEffect(() => {
        if (isFocused || isMobileSearchOpen) {
            positionResults();
            window.addEventListener('scroll', positionResults);
            window.addEventListener('resize', positionResults);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            window.removeEventListener('scroll', positionResults);
            window.removeEventListener('resize', positionResults);
            document.body.style.overflow = '';
        };
    }, [isFocused,isMobileSearchOpen]);

const [hover, setHover] = useState(false);
    return (
        <div
            className={`searchbar-container ${isFocused ? 'focused' : ''}`}
            ref={searchbarRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{marginRight: '20px'}}
        >
            {windowWidth <= 990 && (
                <div
                    className="mobile-search-icon"
                    style={{ color: isMobileSearchOpen ? '#47b149' : 'black' }}
                    onClick={handleMobileSearchToggle}
                >
                    <IoSearch className="search-icon" />
                </div>
            )}

            {windowWidth > 990 && (
                <SearchInput
                    query={query}
                    suggestion={suggestion}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleInputFocus}
                    onClear={clearInput}
                    onVoice={handleVoiceInput}
                    isListening={isListening}
                    translation={translation}
                    inputRef={inputRef}
                    onSuggestionClick={handleSuggestionClick}
                />
            )}

            {(isFocused || isMobileSearchOpen) && (
                <div className="typeahead-dropdown" style={resultsStyle}   ref={dropdownRef}>
                    <div style={{maxWidth: '1630px'}}>
                        <button className={hover && windowWidth > 990  ? "hover-close-button" : "close-button"}  onMouseEnter={() => setHover(true)}
                                onMouseLeave={() => setHover(false)} onClick={handleCloseDropdown}>
                            {translation('close')} <RiCloseLargeFill />
                        </button>
                    </div>

                    {isMobileSearchOpen && (
                        <SearchInput
                            query={query}
                            suggestion={suggestion}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            onFocus={handleInputFocus}
                            onClear={clearInput}
                            onVoice={handleVoiceInput}
                            isListening={isListening}
                            translation={translation}
                            inputRef={inputRef}
                            onSuggestionClick={handleSuggestionClick}
                        />
                    )}

                    {apiData && (apiData?.products || apiData?.services) && (
                        <div className="dropdown-content">
                            {apiData && (
                                <>
                                    {query ? (
                                        <SectionsLayout
                                            apiData={apiData}
                                            translation={translation}
                                            query={query}
                                            viewAllRedirection={viewAllRedirection}
                                            originUrlRedirection={originUrlRedirection}
                                            handleProductClick={handleProductClick}
                                            handleInputChange={handleInputChange}
                                        />
                                    ) : (
                                        <DefaultLayout
                                            apiData={apiData}
                                            translation={translation}
                                            query={query}
                                            recentSearchesList={recentSearchesList}
                                            removeSearch={removeSearch}
                                            handleInputChange={handleInputChange}
                                            handleProductClick={handleProductClick}
                                            originUrlRedirection={originUrlRedirection}
                                            lang={lang}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {!isLoading && !(apiData?.products || apiData?.services) && (
                        <div className="dropdown-content no-results-content">
                            <img src={NoResults} alt="No results" />
                            <p>{translation('noResultsFound')}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchBar;
